import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { fade, capitalize } from '@material-ui/core';
import MuiSwitch from '@material-ui/core/Switch';

import { makeStyles, withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';

const MUI_COLORS = ['default', 'primary', 'secondary'];

export const COLORS = [
  ...MUI_COLORS,
  'accentBlue',
  'accentBlue2',
  'lightGray',
  'primaryGray',
  'red',
  'white',
  'accentGreen',
  'orange',
];

const getColorProp = (color) => {
  if (MUI_COLORS.includes(color)) {
    return color;
  }

  return 'default';
};

const getSwitchBaseColor = (theme, color) => {
  return {
    '&.Mui-checked': {
      color,
      '&:hover': {
        backgroundColor: fade(color, theme.palette.action.hoverOpacity),
        '@media (hover: none)': {
          backgroundColor: 'transparent',
        },
      },
    },
    '&.Mui-disabled': {
      color: theme.palette.type === 'light' ? theme.palette.grey[400] : theme.palette.grey[800],
    },
    '&.Mui-checked + .MuiSwitch-track': {
      backgroundColor: color,
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      backgroundColor:
        theme.palette.type === 'light' ? theme.palette.common.black : theme.palette.common.white,
    },
  };
};

const useCustomStyles = makeStyles(theme => ({
  checked: {},
  disabled: {},
  track: {},
  switchBaseColorPrimary: getSwitchBaseColor(theme, theme.palette.primary.main),
  switchBaseColorSecondary: getSwitchBaseColor(theme, theme.palette.secondary.main),
  switchBaseColorAccentBlue: getSwitchBaseColor(theme, theme.palette.facet.accentBlue),
  switchBaseColorAccentBlue2: getSwitchBaseColor(theme, theme.palette.facet.accentBlue2),
  switchBaseColorDarkGray: getSwitchBaseColor(theme, theme.palette.facet.darkGray),
  switchBaseColorLightGray: getSwitchBaseColor(theme, theme.palette.facet.lightGray),
  switchBaseColorPrimaryGray: getSwitchBaseColor(theme, theme.palette.facet.primaryGray),
  switchBaseColorRed: getSwitchBaseColor(theme, theme.palette.facet.red),
  switchBaseColorOrange: getSwitchBaseColor(theme, theme.palette.facet.orange),
  switchBaseColorSeafoam: getSwitchBaseColor(theme, theme.palette.facet.seafoam),
  switchBaseColorAccentGreen: getSwitchBaseColor(theme, theme.palette.facet.accentGreen),
  switchBaseColorWhite: getSwitchBaseColor(theme, theme.palette.common.white),
}));

const styles = () => ({
  root: {},
  checked: {},
  disabled: {},
});

const SwitchControl = React.forwardRef(
  ({
    className,
    classes,
    color,
    inverted,
    onChange,
    value,
    helperText,
    ...rest
  }, ref) => {
    const {
      root: rootClass,
      switchBase: switchBaseClass,
      ...restClasses
    } = classes;
    const customClasses = useCustomStyles();

    return (
      <MuiSwitch
        classes={{
          ...restClasses,
          root: classNames(rootClass, className),
          switchBase: classNames(switchBaseClass, customClasses[`switchBaseColor${capitalize(color)}`]),
        }}
        checked={value}
        color={getColorProp(color)}
        onChange={(evt) => {
          if (inverted) {
            return onChange(!evt.target.checked);
          }

          return onChange(evt.target.checked);
        }}
        ref={ref}
        variant="outlined"
        {...rest}
      />
    );
  },
);

SwitchControl.defaultProps = {
  className: '',
  color: 'primary',
  inverted: null,
};

SwitchControl.propTypes = {
  className: PropTypes.string.isRequired,
  classes: PropTypes.object,
  color: PropTypes.oneOf(COLORS),
  inverted: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.bool.isRequired,
};

export default withStyles(styles)(SwitchControl);
